import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { analyticsTrackEvent } from '../../../../../core/utils/analytics';
import { selectors, operations } from '../../../../store/materials';
import * as analyticsTypes from '../../../../../core/constants/analytics';
import * as userSelectors from '../../../../../core/selectors/user';
import * as classRoomSelectors from '../../../../../core/selectors/classroom';
import {
  canEditMaterials,
  canViewMaterials,
  canAccessEducatorArea,
  createPermissionDeniedToast,
  MATERIAL_PERMISSIONS,
} from '@core/materials/permissions';
import {
  isPermissionDeniedError,
  canUserViewMaterial,
} from '@core/materials/selectors';
import showToast from '@core/toasts/operations/showToast.operation';
import Material from './Material';
import {
  goToMaterials,
  goToEducatorArea,
} from '../../../../actions/navigation';

const baseSelector = createStructuredSelector({
  user: userSelectors.user,
  currentSectionId: selectors.currentSectionId,
  currentSectionTitle: selectors.currentSectionTitle,
  currentSpaceName: classRoomSelectors.classRoomName,
  spaceId: classRoomSelectors.currentClassRoomId,
  activeClassroom: classRoomSelectors.currentClassRoomId,
  hasFetchedSections: selectors.hasFetchedSections,
  section: selectors.currentSection,
  sectionIds: selectors.topLevelMaterialIds,
  sectionEducatorOnlyIds: selectors.topLevelMaterialEducatorOnlyIds,
  editing: selectors.editing,
  saving: selectors.isSavingSection,
  leaveDestinationPath: selectors.leaveDestinationPath,
  isDeletingSection: selectors.isDeletingSection,
  isDeletingFolder: selectors.isDeletingFolder,
  hasAtLeastOneChildSection: selectors.hasAtLeastOneChildSection,
  fallbackMaterialId: selectors.getFirstMaterialId,
  fallbackEducatorMaterialId: selectors.getFirstEducatorMaterialId,
  isStudent: classRoomSelectors.getIsStudent,
});

const mapStateToProps = (state, ownProps) => {
  const base = baseSelector(state);
  const { isStudent, ...rest } = base;

  // In educator area: if user has educator area permission, they can view
  // Otherwise, use normal view permission
  const canView = ownProps.educatorOnly
    ? canAccessEducatorArea(state)
    : canViewMaterials(state);

  return {
    ...rest,
    isInstructor: !isStudent,
    canViewMaterials: canView,
    isPermissionDeniedError: isPermissionDeniedError(state),
    canUserViewMaterial: canUserViewMaterial(state, base.currentSectionId),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  dispatchAnalytics: (sectionId, activeClassroom) => {
    // https://github.com/AulaEducation/aula/issues/13216
    // When loading the journey without a material in the URL, we may render the material section without a material id.
    if (sectionId) {
      analyticsTrackEvent(analyticsTypes.MATERIALS_VIEW, {
        itemType: analyticsTypes.MATERIAL_ITEM_TYPE,
        itemId: sectionId,
        activeClassroom,
      });
    }
  },
  onLeaveWithoutSaving: (destinationPath, keepEditing) =>
    dispatch(
      operations.leaveWithoutSaving(
        destinationPath,
        keepEditing,
        ownProps.educatorOnly
      )
    ),
  goToFallbackSection: (spaceId, fallbackId) => {
    dispatch(
      ownProps.educatorOnly
        ? goToEducatorArea(spaceId, fallbackId)
        : goToMaterials(spaceId, fallbackId)
    );
  },
  onPermissionDenied: (permissionKey) => {
    analyticsTrackEvent(analyticsTypes.MATERIALS_PERMISSION_DENIED, {
      permissionKey,
    });
    dispatch(showToast(createPermissionDeniedToast(permissionKey)));
  },
});

const mergeProps = (
  { fallbackMaterialId, fallbackEducatorMaterialId, spaceId, ...stateProps },
  { goToFallbackSection, ...dispatchProps },
  ownProps
) => ({
  ...ownProps,
  ...dispatchProps,
  ...stateProps,
  dispatchAnalytics: () => {
    dispatchProps.dispatchAnalytics(
      stateProps.currentSectionId,
      stateProps.activeClassroom ||
        (ownProps.params ? ownProps.params.classId : undefined)
    );
  },
  goToFallbackSection: () => {
    goToFallbackSection(
      spaceId,
      ownProps.educatorOnly ? fallbackEducatorMaterialId : fallbackMaterialId
    );
  },
  fallbackId: ownProps.educatorOnly
    ? fallbackEducatorMaterialId
    : fallbackMaterialId,
});

const ConnetedMaterialContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(Material);

const MaterialContainer = (props) => {
  const { webAppFtHeaderNavigationMode112025 } = useFlags();
  const isBackButtonRouteAllowed = webAppFtHeaderNavigationMode112025 === true;

  return (
    <ConnetedMaterialContainer
      {...props}
      isBackButtonRouteAllowed={isBackButtonRouteAllowed}
    />
  );
};

export default MaterialContainer;
